import type {
  ICandidatesService,
  QuestCandidate,
  ReferCandidateInput,
  WorkerJourney,
} from "@/services/interfaces/ICandidatesService";
import { ReferCandidateError } from "@/lib/questErrors";
import type { ExpressInterestResult } from "@/lib/types";

/**
 * How many leaf tasks a mock quest has. The real eligibility rules live in the
 * database (supabase/tests/worker_express_interest_test.sql owns them); this
 * only has to model the two things a component test can observe — that a first
 * tap accepts tasks, and that a re-tap accepts none. Exported so tests assert
 * against the same constant rather than restating the literal.
 */
export const MOCK_LEAF_TASK_COUNT = 3;

const MOCK_CANDIDATES: QuestCandidate[] = [
  {
    id: "mock-internal-1",
    memberId: "did:privy:mock-ada",
    profileId: "did:privy:mock-ada",
    fullName: "Ada Benítez",
    locationCity: "Asunción",
    locationCountry: "Paraguay",
    skills: ["React", "TypeScript", "Node.js"],
    employmentTitle: "Frontend Developer",
    score: 0.95,
    availabilityAnswer: null,
    referredByName: null,
  },
  {
    // Carlos came in via the Headhunter worker but has since JOINED weXare — so to
    // the creator he's just a member, with no "external" marker and no LinkedIn.
    // (Raw, not-yet-joined prospects never reach the creator; they're admin-only.)
    // Like a real converted row, he has no member_id; his profile is reached
    // through profileId (converted_user_id).
    id: "mock-external-1",
    memberId: null,
    profileId: "did:privy:mock-carlos",
    fullName: "Carlos Cardozo",
    locationCity: "Asunción",
    locationCountry: "Paraguay",
    skills: ["Software Engineering", "React"],
    employmentTitle: "Co-Founder",
    score: 0.82,
    // Carlos came through outreach and answered step 4 — the creator sees the
    // answer, never the channel it was asked on.
    availabilityAnswer: "yes",
    referredByName: null,
  },
];

// Quest IDs that have seeded suggestions in mock/offline mode. Any other quest
// returns an empty list so the empty state is exercisable.
const SEEDED_QUEST_IDS = new Set<string>(["mock-quest-001"]);

/**
 * DEV-783 steps 5–6. Held on the seeded quest so offline mode can exercise the
 * waiting screen, which is the state a real worker spends the most time in.
 * `selectedAt` stays null until `select()` is called, so the mock walks the
 * same one-way transition the database enforces.
 */
const MOCK_JOURNEYS = new Map<string, WorkerJourney>([
  [
    "mock-quest-001",
    {
      candidateId: "mock-external-1",
      status: "applied",
      availabilityAnswer: "yes",
      // Two hours ago, so the held countdown shows a realistic 70 hours rather
      // than a suspiciously round 72.
      appliedAt: new Date(Date.now() - 2 * 3_600_000).toISOString(),
      selectedAt: null,
    },
  ],
]);

export class MockCandidatesService implements ICandidatesService {
  /**
   * Optional so the unit tests that exercise this service alone need nothing.
   * The registry passes the negotiation mock: an application writes proposals
   * in the database, and the mock has to leave the same trace where the
   * apply buttons read it back.
   */
  constructor(
    private readonly proposals?: { recordWorkerInterest(questId: string, taskIds?: string[]): Promise<void> },
  ) {}

  async listForQuest(questId: string): Promise<QuestCandidate[]> {
    return SEEDED_QUEST_IDS.has(questId) ? MOCK_CANDIDATES : [];
  }

  async journeyForQuest(questId: string): Promise<WorkerJourney | null> {
    return MOCK_JOURNEYS.get(questId) ?? null;
  }

  // The referral code is accepted and ignored: this fixture has no link table
  // and no current-user identity, so it cannot model resolution, self-referral
  // or first-attribution-wins. supabase/tests/quest_referral_links_test.sql
  // owns those — same division of labour as the refusals below.
  async applyToQuest(questId: string, _referralCode?: string | null): Promise<string> {
    // Models ONLY the RPC's happy path and its idempotency. The refusals
    // worker_apply_to_quest enforces — creator applying to their own quest, a
    // draft/finished quest, the hourly volume cap — are deliberately not
    // modelled here: this fixture has no current-user identity and no quest
    // status to check them against. Unlike select() below, which does mirror
    // the RPC's refusal message, a mock-backed test cannot exercise those
    // paths; supabase/tests/worker_apply_to_quest_test.sql owns them.
    //
    // Idempotent like the RPC: a second tap returns the same candidacy rather
    // than minting a new one or resetting the clock.
    const existing = MOCK_JOURNEYS.get(questId);
    if (existing) {
      if (existing.status !== "selected" && existing.status !== "applied") {
        existing.status = "applied";
        existing.appliedAt = new Date().toISOString();
      }
      return existing.candidateId;
    }
    const journey: WorkerJourney = {
      candidateId: `mock-self-${questId}`,
      status: "applied",
      availabilityAnswer: null,
      appliedAt: new Date().toISOString(),
      selectedAt: null,
    };
    MOCK_JOURNEYS.set(questId, journey);
    return journey.candidateId;
  }

  async expressInterestInQuest(
    questId: string,
    referralCode?: string | null,
  ): Promise<ExpressInterestResult> {
    // Derived from MOCK_JOURNEYS itself, read BEFORE calling applyToQuest,
    // rather than tracked in a second, separate set: a candidacy already
    // existing (any status — including the "mock-quest-001" fixture seeded
    // at module load in an already-`applied` state) is exactly the signal
    // that the tasks were already accepted on a prior tap. A second piece of
    // state tracking the same fact independently can drift from this one;
    // reading MOCK_JOURNEYS directly cannot.
    const alreadyAccepted = MOCK_JOURNEYS.has(questId);
    const candidateId = await this.applyToQuest(questId, referralCode);

    // Record what this tap took, in the SAME place the per-task method reads.
    // The quest-level RPC proposes on EVERY eligible leaf task, so a later
    // per-task tap on one of those tasks is a re-tap the database would skip.
    // Without this line the two methods kept separate skip-state and a
    // mock-backed run reported a fresh success for a write that would have
    // written nothing — a false confirmation toast read as the feature
    // working. `wholeQuest` rather than a list of ids because this fixture
    // has no task ids of its own (see MOCK_LEAF_TASK_COUNT): "all of them" is
    // exactly what the quest-level call means.
    // Unconditional, including on the `alreadyAccepted` branch: that branch
    // reports "0 accepted, every leaf skipped", which says the worker already
    // holds them all. Both branches therefore leave the same fact behind.
    this.acceptedFor(questId).wholeQuest = true;
    if (!alreadyAccepted) await this.proposals?.recordWorkerInterest(questId);

    return {
      candidateId,
      tasksAccepted: alreadyAccepted ? 0 : MOCK_LEAF_TASK_COUNT,
      tasksSkipped: alreadyAccepted ? MOCK_LEAF_TASK_COUNT : 0,
    };
  }

  // Which tasks this mock worker has already been accepted onto, per quest.
  // Tracked separately from MOCK_JOURNEYS (which only records quest-level
  // candidacy) because the thing expressInterestInTasks must model — a second
  // tap on the SAME task reporting 0 accepted — is per-task, not per-quest: a
  // first apply to task A must not silently "use up" the candidacy for task B.
  // BOTH express-interest methods write here, so the skip state is per
  // WORKER-AND-QUEST and not per method.
  private mockAcceptedTaskIds = new Map<string, { wholeQuest: boolean; taskIds: Set<string> }>();

  private acceptedFor(questId: string) {
    const existing = this.mockAcceptedTaskIds.get(questId);
    if (existing) return existing;
    const fresh = { wholeQuest: false, taskIds: new Set<string>() };
    this.mockAcceptedTaskIds.set(questId, fresh);
    return fresh;
  }

  async expressInterestInTasks(
    questId: string,
    taskIds: string[],
    referralCode?: string | null,
  ): Promise<ExpressInterestResult> {
    // Mirrors the RPC's skip semantics rather than always claiming success: a
    // second tap on the same task must report 0 accepted, or the mock-backed
    // surfaces would show a confirmation toast for a write the real database
    // would have skipped. `wholeQuest` covers the cross-method case — a
    // prior whole-quest tap already proposed on every leaf, this one
    // included.
    const accepted = this.acceptedFor(questId);
    const fresh = accepted.wholeQuest
      ? []
      : taskIds.filter((id) => !accepted.taskIds.has(id));

    const candidateId = await this.applyToQuest(questId, referralCode);

    fresh.forEach((id) => accepted.taskIds.add(id));
    await this.proposals?.recordWorkerInterest(questId, fresh);

    return {
      candidateId,
      tasksAccepted: fresh.length,
      tasksSkipped: taskIds.length - fresh.length,
    };
  }

  async select(candidateId: string): Promise<string> {
    const journey = [...MOCK_JOURNEYS.values()].find((j) => j.candidateId === candidateId);
    // Mirrors the RPC: one message for "not yours", "not found" and "wrong
    // status", so nothing built against the mock relies on a distinction the
    // real database deliberately refuses to make.
    if (!journey || journey.status !== "applied") throw new Error("cannot select candidate");
    const selectedAt = new Date().toISOString();
    journey.status = "selected";
    journey.selectedAt = selectedAt;
    return selectedAt;
  }

  private referralCodes = new Map<string, string>();

  async createReferralLink(questId: string): Promise<string> {
    // Stable per quest, like the real UNIQUE (quest_id, referrer_id): a test
    // that opens the share modal twice must see one link, not two.
    const existing = this.referralCodes.get(questId);
    if (existing) return existing;
    const alphabet = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
    const code = Array.from(
      { length: 10 },
      () => alphabet[Math.floor(Math.random() * alphabet.length)],
    ).join("");
    this.referralCodes.set(questId, code);
    return code;
  }

  async referCandidate(input: ReferCandidateInput): Promise<void> {
    // The refusals a demo can actually reach from the form; the real RPC is
    // the authority (see 20260923100000). Success writes nothing here — the
    // real one's VOID return means the UI never reads a result either.
    if (!input.fullName.trim()) {
      throw new ReferCandidateError("referral_name_required", "tell us their name");
    }
    if (!input.email?.trim() && !input.phone?.trim()) {
      throw new ReferCandidateError("referral_contact_required", "add an email or a phone number");
    }
    if (input.phone?.trim() && !input.phone.trim().startsWith("+")) {
      throw new ReferCandidateError("referral_phone_invalid", "phone must be in international format");
    }
  }
}
